import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const err = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.errors?.join("; ") || d?.message || "Request failed";
};

const BenefitRow = ({ b, autoTypes, onChange, onRemove }) => (
  <div className="grid grid-cols-1 gap-2 rounded-md border border-limestone bg-white p-3 md:grid-cols-12" data-testid={`benefit-row-${b.id}`}>
    <Input className="md:col-span-2" value={b.id} placeholder="id" onChange={(e) => onChange({ ...b, id: e.target.value.trim() })} data-testid="benefit-id" />
    <Input className="md:col-span-3" value={b.title} placeholder="Title" onChange={(e) => onChange({ ...b, title: e.target.value })} data-testid="benefit-title" />
    <Input className="md:col-span-3" value={b.description || ""} placeholder="Description" onChange={(e) => onChange({ ...b, description: e.target.value })} data-testid="benefit-description" />
    <select className="rounded-md border border-input bg-white px-2 text-sm md:col-span-1" value={b.mode} onChange={(e) => onChange({ ...b, mode: e.target.value })} data-testid="benefit-mode">
      <option value="informational">Informational</option>
      <option value="manual">Manual</option>
      <option value="automatic">Automatic</option>
    </select>
    <select className="rounded-md border border-input bg-white px-2 text-sm md:col-span-1" value={b.type || "general"} onChange={(e) => onChange({ ...b, type: e.target.value })} data-testid="benefit-type">
      <option value="general">General</option>
      {Object.entries(autoTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
    <Input className="md:col-span-1" type="number" min={1} value={b.limit_per_period ?? ""} placeholder="limit" onChange={(e) => onChange({ ...b, limit_per_period: e.target.value === "" ? null : Number(e.target.value) })} data-testid="benefit-limit" />
    <Button variant="ghost" size="icon" className="md:col-span-1" onClick={onRemove} data-testid="benefit-remove"><Trash2 className="h-4 w-4" /></Button>
  </div>
);

const TierEditor = ({ tier, autoTypes, onChange }) => {
  const setBenefit = (i, nb) => onChange({ ...tier, benefits: tier.benefits.map((b, j) => (j === i ? nb : b)) });
  return (
    <div className="rounded-lg border border-limestone bg-limestone/30 p-4" data-testid={`tier-editor-${tier.code.toLowerCase()}`}>
      <div className="grid gap-3 md:grid-cols-3">
        <div>
          <label className="text-[11px] uppercase tracking-wide text-warmgrey">{tier.code} label</label>
          <Input value={tier.label} onChange={(e) => onChange({ ...tier, label: e.target.value })} data-testid={`tier-label-${tier.code.toLowerCase()}`} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-warmgrey">Threshold (sq ft / quarter)</label>
          <Input type="number" min={0} disabled={tier.code === "STANDARD"} value={tier.threshold_sqft} onChange={(e) => onChange({ ...tier, threshold_sqft: Number(e.target.value) })} data-testid={`tier-threshold-${tier.code.toLowerCase()}`} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-warmgrey">Description</label>
          <Input value={tier.description || ""} onChange={(e) => onChange({ ...tier, description: e.target.value })} data-testid={`tier-description-${tier.code.toLowerCase()}`} />
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {tier.benefits.map((b, i) => (
          <BenefitRow key={i} b={b} autoTypes={autoTypes} onChange={(nb) => setBenefit(i, nb)} onRemove={() => onChange({ ...tier, benefits: tier.benefits.filter((_, j) => j !== i) })} />
        ))}
        <Button variant="outline" size="sm" onClick={() => onChange({ ...tier, benefits: [...tier.benefits, { id: "", title: "", description: "", mode: "informational", type: "general", limit_per_period: null }] })} data-testid={`tier-add-benefit-${tier.code.toLowerCase()}`}>
          <Plus className="mr-1 h-3 w-3" /> Add benefit
        </Button>
      </div>
    </div>
  );
};

export default function AdminPartnerProgram() {
  const [ov, setOv] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [effective, setEffective] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/admin/partner-program");
    setOv(r.data);
    setDraft(r.data.draft);
    setPreview(null);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const createDraft = (base) => run(async () => { await api.post("/admin/partner-program/drafts", { base }); await load(); }, "Draft created");
  const saveDraft = () => run(async () => {
    const r = await api.patch(`/admin/partner-program/drafts/${draft.id}`, { review: draft.review, tiers: draft.tiers, change_note: draft.change_note || "" });
    setDraft(r.data);
    if (r.data.validation_errors?.length) toast.warning(r.data.validation_errors.join("; "));
  }, "Draft saved");
  const doPreview = () => run(async () => {
    await api.patch(`/admin/partner-program/drafts/${draft.id}`, { review: draft.review, tiers: draft.tiers, change_note: draft.change_note || "" });
    const r = await api.get(`/admin/partner-program/drafts/${draft.id}/preview`);
    setPreview(r.data);
  });
  const publish = () => run(async () => {
    await api.post(`/admin/partner-program/drafts/${draft.id}/publish`, { effective_from: effective ? new Date(effective).toISOString() : null, change_note: note });
    await load();
  }, "Partner Program published");
  const discard = () => run(async () => { await api.delete(`/admin/partner-program/drafts/${draft.id}`); await load(); }, "Draft discarded");

  if (!ov) return <p className="p-8 text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="admin-partner-program">
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-warmgrey">Dealer incentives</p>
        <h1 className="mt-2 font-display text-4xl font-light text-charcoal">Partner Program</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Draft → Preview → Publish. Published versions drive the dealer journey and benefit eligibility. Tier changes remain a governed admin review; no benefit is active until published here.</p>
      </div>

      <section className="rounded-lg border border-limestone bg-white p-5" data-testid="published-config">
        <h2 className="text-base font-medium text-charcoal md:text-lg">Published</h2>
        {ov.published ? (
          <div className="mt-2 text-sm text-charcoal">
            <p data-testid="published-version">Version {ov.published.version} · effective {new Date(ov.published.effective_from).toLocaleString()}</p>
            <ul className="mt-2 grid gap-2 md:grid-cols-4">
              {ov.published.tiers.map((t) => (
                <li key={t.code} className="rounded-md bg-limestone/40 p-3 text-xs" data-testid={`published-tier-${t.code.toLowerCase()}`}>
                  <p className="font-medium">{t.label}</p>
                  <p className="text-warmgrey">{t.threshold_sqft.toLocaleString()} sq ft</p>
                  <p className="mt-1 text-muted-foreground">{t.benefits.length} benefit(s)</p>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="published-none">Nothing published yet — dealers see the existing pricing thresholds with no benefits.</p>
        )}
      </section>

      <section className="rounded-lg border border-terracotta/30 bg-white p-5" data-testid="draft-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-medium text-charcoal md:text-lg">Draft</h2>
          {!draft && (
            <div className="flex gap-2">
              <Button onClick={() => createDraft("published")} disabled={busy} data-testid="create-draft-btn">New draft</Button>
              {!ov.published && <Button variant="outline" onClick={() => createDraft("default")} disabled={busy} data-testid="create-default-draft-btn">Start from defaults</Button>}
            </div>
          )}
        </div>
        {draft && (
          <div className="mt-4 space-y-4">
            <p className="text-xs text-warmgrey" data-testid="draft-version">Draft version {draft.version}</p>
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-warmgrey">Review period</label>
                <Input value="quarterly" disabled data-testid="review-period" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-warmgrey">Grace days after review</label>
                <Input type="number" min={0} value={draft.review?.grace_days ?? 30} onChange={(e) => setDraft({ ...draft, review: { ...draft.review, grace_days: Number(e.target.value) } })} data-testid="review-grace-days" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-warmgrey">Auto-promote</label>
                <Input value="Never (governed review)" disabled data-testid="review-auto-promote" />
              </div>
            </div>
            {draft.tiers.map((t, i) => (
              <TierEditor key={t.code} tier={t} autoTypes={ov.automatic_types} onChange={(nt) => setDraft({ ...draft, tiers: draft.tiers.map((x, j) => (j === i ? nt : x)) })} />
            ))}
            <Textarea rows={2} placeholder="Change note" value={draft.change_note || ""} onChange={(e) => setDraft({ ...draft, change_note: e.target.value })} data-testid="draft-change-note" />
            {draft.validation_errors?.length > 0 && (
              <ul className="rounded-md border border-terracotta/40 bg-terracotta/5 p-3 text-xs text-terracotta" data-testid="draft-validation-errors">
                {draft.validation_errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={saveDraft} disabled={busy} data-testid="save-draft-btn">Save draft</Button>
              <Button variant="outline" onClick={doPreview} disabled={busy} data-testid="preview-draft-btn">Preview</Button>
              <Button variant="ghost" onClick={discard} disabled={busy} data-testid="discard-draft-btn">Discard</Button>
            </div>
            {preview && (
              <div className="rounded-md border border-limestone bg-limestone/30 p-4" data-testid="draft-preview">
                <p className="text-sm font-medium text-charcoal">Preview {preview.publishable ? "— publishable" : "— not publishable"}</p>
                {preview.validation_errors.length > 0 && <ul className="mt-1 text-xs text-terracotta">{preview.validation_errors.map((e) => <li key={e}>{e}</li>)}</ul>}
                <ol className="mt-3 grid grid-cols-4 gap-2">
                  {preview.dealer_preview.map((t) => (
                    <li key={t.code} className="rounded-md border border-limestone bg-white p-2 text-xs">
                      <p className="font-medium">{t.label}</p>
                      <p className="text-warmgrey">{t.threshold_sqft.toLocaleString()} sq ft</p>
                      <ul className="mt-1 text-muted-foreground">{t.benefits.map((b) => <li key={b.id}>• {b.title} <span className="uppercase text-[9px]">{b.mode}</span></li>)}</ul>
                    </li>
                  ))}
                </ol>
                {preview.diff_vs_published && (
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="draft-diff">{preview.diff_vs_published.changes.length} change(s) vs published</p>
                )}
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-warmgrey">Effective from (optional)</label>
                    <Input type="datetime-local" value={effective} onChange={(e) => setEffective(e.target.value)} data-testid="publish-effective-from" />
                  </div>
                  <Input className="flex-1" placeholder="Publish note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="publish-note" />
                  <Button onClick={publish} disabled={busy || !preview.publishable} data-testid="publish-draft-btn">{busy ? "Publishing…" : "Publish"}</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-limestone bg-white p-5" data-testid="version-history">
        <h2 className="text-base font-medium text-charcoal md:text-lg">Version history</h2>
        <ul className="mt-2 divide-y divide-limestone text-sm">
          {ov.history.map((v) => (
            <li key={v.id} className="flex flex-wrap justify-between gap-2 py-2" data-testid={`history-v${v.version}`}>
              <span>v{v.version} · <span className="uppercase text-xs text-warmgrey">{v.status}</span>{v.change_note ? ` · ${v.change_note}` : ""}</span>
              <span className="text-xs text-muted-foreground">{v.published_at ? `published ${new Date(v.published_at).toLocaleString()}` : `created ${new Date(v.created_at).toLocaleString()}`}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
